import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, FileSpreadsheet } from "lucide-react";

export const Route = createFileRoute("/_authenticated/gestion/iva")({
  component: IvaPage,
});

type FacturaRow = {
  id: string;
  numero_completo: string;
  fecha_emision: string;
  base_imponible: number;
  iva_porcentaje: number;
  iva_importe: number;
  total: number;
  anulada: boolean;
  cliente_snapshot: { nombre?: string; email?: string } | null;
};

const QUARTERS = [
  { value: "1", label: "1T (Enero - Marzo)", start: [0, 1], end: [2, 31] },
  { value: "2", label: "2T (Abril - Junio)", start: [3, 1], end: [5, 30] },
  { value: "3", label: "3T (Julio - Septiembre)", start: [6, 1], end: [8, 30] },
  { value: "4", label: "4T (Octubre - Diciembre)", start: [9, 1], end: [11, 31] },
] as const;

const fmt = (n: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

function pad(n: number) { return String(n).padStart(2, "0"); }

function IvaPage() {
  const now = new Date();
  const [anio, setAnio] = useState<string>(String(now.getFullYear()));
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
  const [trimestre, setTrimestre] = useState<string>(String(currentQuarter));
  const [rows, setRows] = useState<FacturaRow[]>([]);
  const [loading, setLoading] = useState(false);

  const { desde, hasta } = useMemo(() => {
    const q = QUARTERS.find((x) => x.value === trimestre)!;
    const y = Number(anio);
    const d = new Date(y, q.start[0], q.start[1]);
    const h = new Date(y, q.end[0], q.end[1]);
    return {
      desde: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      hasta: `${h.getFullYear()}-${pad(h.getMonth() + 1)}-${pad(h.getDate())}`,
    };
  }, [anio, trimestre]);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("facturas")
      .select("id, numero_completo, fecha_emision, base_imponible, iva_porcentaje, iva_importe, total, anulada, cliente_snapshot")
      .gte("fecha_emision", desde)
      .lte("fecha_emision", hasta)
      .order("fecha_emision");
    if (error) toast.error(error.message);
    setRows(((data ?? []) as unknown) as FacturaRow[]);
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [desde, hasta]);

  const emitidas = rows.filter((r) => !r.anulada);
  const anuladas = rows.filter((r) => r.anulada);

  const porTipo = useMemo(() => {
    const map = new Map<number, { base: number; cuota: number; total: number; num: number }>();
    for (const r of emitidas) {
      const key = Number(r.iva_porcentaje);
      const acc = map.get(key) ?? { base: 0, cuota: 0, total: 0, num: 0 };
      acc.base += Number(r.base_imponible);
      acc.cuota += Number(r.iva_importe);
      acc.total += Number(r.total);
      acc.num += 1;
      map.set(key, acc);
    }
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [emitidas]);

  const totalBase = porTipo.reduce((s, [, v]) => s + v.base, 0);
  const totalCuota = porTipo.reduce((s, [, v]) => s + v.cuota, 0);
  const totalFacturado = porTipo.reduce((s, [, v]) => s + v.total, 0);

  function exportarDetalle() {
    const header = ["Numero", "Fecha", "Cliente", "Email", "Base imponible", "% IVA", "Cuota IVA", "Total", "Anulada"];
    const lines = [header.join(";")];
    for (const r of rows) {
      const cli = (r.cliente_snapshot as any) ?? {};
      lines.push([
        r.numero_completo,
        r.fecha_emision,
        `"${(cli.nombre ?? "").replace(/"/g, '""')}"`,
        cli.email ?? "",
        r.base_imponible.toFixed(2).replace(".", ","),
        r.iva_porcentaje.toFixed(2).replace(".", ","),
        r.iva_importe.toFixed(2).replace(".", ","),
        r.total.toFixed(2).replace(".", ","),
        r.anulada ? "SI" : "NO",
      ].join(";"));
    }
    downloadCsv(`facturas_${anio}_T${trimestre}.csv`, lines.join("\n"));
  }

  function exportarResumen() {
    const lines = [
      ["Ejercicio", anio].join(";"),
      ["Trimestre", `T${trimestre}`].join(";"),
      ["Periodo", `${desde} → ${hasta}`].join(";"),
      "",
      ["% IVA", "Nº facturas", "Base imponible", "Cuota IVA", "Total"].join(";"),
    ];
    for (const [pct, v] of porTipo) {
      lines.push([
        `${pct.toFixed(2).replace(".", ",")}%`,
        v.num,
        v.base.toFixed(2).replace(".", ","),
        v.cuota.toFixed(2).replace(".", ","),
        v.total.toFixed(2).replace(".", ","),
      ].join(";"));
    }
    lines.push("");
    lines.push(["TOTAL", emitidas.length, totalBase.toFixed(2).replace(".", ","), totalCuota.toFixed(2).replace(".", ","), totalFacturado.toFixed(2).replace(".", ",")].join(";"));
    downloadCsv(`resumen_iva_${anio}_T${trimestre}.csv`, lines.join("\n"));
  }

  function downloadCsv(name: string, content: string) {
    const blob = new Blob(["\ufeff" + content], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  }

  const anios: string[] = [];
  for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 5; y--) anios.push(String(y));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <FileSpreadsheet className="h-6 w-6" /> Informe trimestral de IVA
        </h1>
        <p className="text-sm text-muted-foreground">
          Resumen para el modelo 303 a partir de las facturas emitidas. Se agrupa por tipo de IVA repercutido.
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-4 p-4">
          <div>
            <Label>Ejercicio</Label>
            <Select value={anio} onValueChange={setAnio}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>{anios.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Trimestre</Label>
            <Select value={trimestre} onValueChange={setTrimestre}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                {QUARTERS.map((q) => <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-muted-foreground">
            Periodo: <span className="font-medium">{desde}</span> → <span className="font-medium">{hasta}</span>
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={exportarResumen} disabled={loading || porTipo.length === 0}>
              <Download className="mr-2 h-4 w-4" /> Resumen (CSV)
            </Button>
            <Button onClick={exportarDetalle} disabled={loading || rows.length === 0}>
              <Download className="mr-2 h-4 w-4" /> Detalle facturas (CSV)
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Facturas emitidas</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold">{emitidas.length}</p>{anuladas.length > 0 && <p className="text-xs text-muted-foreground">+ {anuladas.length} anuladas</p>}</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Base imponible</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold">{fmt(totalBase)}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">IVA repercutido</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold">{fmt(totalCuota)}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Total facturado</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold">{fmt(totalFacturado)}</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Desglose por tipo de IVA</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : porTipo.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay facturas emitidas en este periodo.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2">% IVA</th>
                    <th className="py-2 text-right">Nº facturas</th>
                    <th className="py-2 text-right">Base imponible</th>
                    <th className="py-2 text-right">Cuota IVA</th>
                    <th className="py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {porTipo.map(([pct, v]) => (
                    <tr key={pct} className="border-b">
                      <td className="py-2 font-medium">{pct}%</td>
                      <td className="py-2 text-right">{v.num}</td>
                      <td className="py-2 text-right">{fmt(v.base)}</td>
                      <td className="py-2 text-right">{fmt(v.cuota)}</td>
                      <td className="py-2 text-right">{fmt(v.total)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold">
                    <td className="py-2">Total</td>
                    <td className="py-2 text-right">{emitidas.length}</td>
                    <td className="py-2 text-right">{fmt(totalBase)}</td>
                    <td className="py-2 text-right">{fmt(totalCuota)}</td>
                    <td className="py-2 text-right">{fmt(totalFacturado)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Facturas del periodo</CardTitle></CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin facturas.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2">Nº</th>
                    <th className="py-2">Fecha</th>
                    <th className="py-2">Cliente</th>
                    <th className="py-2 text-right">Base</th>
                    <th className="py-2 text-right">% IVA</th>
                    <th className="py-2 text-right">Cuota</th>
                    <th className="py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={"border-b " + (r.anulada ? "text-muted-foreground line-through" : "")}>
                      <td className="py-2 font-mono">{r.numero_completo}</td>
                      <td className="py-2">{r.fecha_emision}</td>
                      <td className="py-2">{(r.cliente_snapshot as any)?.nombre ?? "—"}</td>
                      <td className="py-2 text-right">{fmt(Number(r.base_imponible))}</td>
                      <td className="py-2 text-right">{Number(r.iva_porcentaje)}%</td>
                      <td className="py-2 text-right">{fmt(Number(r.iva_importe))}</td>
                      <td className="py-2 text-right">{fmt(Number(r.total))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Nota: este informe es orientativo y refleja las facturas emitidas en el periodo (fecha de emisión). Contrasta siempre los importes con tu asesoría antes de presentar el modelo 303.
      </p>
    </div>
  );
}